import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmDialog } from '../ui/confirm-dialog'
import { exportSecrets, useImportSecrets } from '../../hooks/use-secrets'
import { cn } from '../../lib/utils'

interface SecretImportExportPanelProps {
  environment: string
  path: string
  isDark: boolean
}

function inputClass(isDark: boolean) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-base outline-none md:text-sm',
    isDark ? 'border-white/[0.08] bg-surface-2 text-neutral-100 placeholder-neutral-600' : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400',
  )
}

export function SecretImportExportPanel({ environment, path, isDark }: SecretImportExportPanelProps) {
  const [importText, setImportText] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false)
  const [exportText, setExportText] = useState('')
  const [exportReason, setExportReason] = useState('')
  const [exportReveal, setExportReveal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const qc = useQueryClient()
  const importSecrets = useImportSecrets()

  async function handleImport() {
    try {
      await importSecrets.mutateAsync({
        environment,
        path,
        content: importText,
        overwrite,
        defaultKind: 'secret',
        reason: 'web import',
      })
      setImportText('')
      toast.success('Import complete')
    } catch (err) {
      toast.error('Import failed', { description: err instanceof Error ? err.message : undefined })
    }
  }

  async function handleExport() {
    if (exportReveal && !exportReason.trim()) {
      toast.error('Reason required for raw export')
      return
    }
    setExporting(true)
    try {
      const result = await exportSecrets({ environment, path, reveal: exportReveal, reason: exportReason || undefined })
      setExportText(result.content)
      qc.invalidateQueries({ queryKey: ['secret-audit'] })
    } catch (err) {
      toast.error('Export failed', { description: err instanceof Error ? err.message : undefined })
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className={cn('grid gap-4 border-t pt-4 md:grid-cols-2', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium"><Upload className="h-4 w-4" /> Import .env</div>
        <textarea className={cn(inputClass(isDark), 'h-32 font-mono')} value={importText} onChange={(e) => setImportText(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> overwrite existing
        </label>
        <button type="button" onClick={() => overwrite ? setConfirmOverwriteOpen(true) : void handleImport()} disabled={!importText.trim() || importSecrets.isPending} className="rounded-md bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-50">
          {importSecrets.isPending ? 'Importing...' : 'Import'}
        </button>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4" /> Export</div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={exportReveal} onChange={(e) => setExportReveal(e.target.checked)} /> reveal raw values</label>
        <input className={inputClass(isDark)} value={exportReason} onChange={(e) => setExportReason(e.target.value)} placeholder="Reason for raw export" />
        <button type="button" onClick={handleExport} disabled={exporting} className="rounded-md bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-50">
          {exporting ? 'Exporting...' : 'Export'}
        </button>
        {exportText ? (
          <>
            <button type="button" onClick={() => setExportText('')} className="rounded-md px-3 py-2 text-sm text-brand-600 hover:bg-brand-500/10">Clear output</button>
            <pre className={cn('max-h-40 overflow-auto rounded-md p-3 text-xs', isDark ? 'bg-surface-2' : 'bg-neutral-100')}>{exportText}</pre>
          </>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirmOverwriteOpen}
        onClose={() => setConfirmOverwriteOpen(false)}
        onConfirm={handleImport}
        title="Overwrite existing secrets?"
        description="Matching keys in this environment and path will be rotated to the imported values."
        confirmLabel="Import overwrite"
        variant="danger"
      />
    </section>
  )
}
